"use client";

import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useWorkspaceContextWithFallback } from "@/contexts/workspaceContext";
import { uploadCoverImage } from "@/components/tailwind/image-upload";
import { blobToFile } from "@/lib/cropImage";
import { useSquareEmojiImage } from "@/hooks/useSquareEmojiImage";
import { toast } from "@/lib/toast";
import { createCustomEmoji, updateCustomEmoji } from "@/services-frontend/workspace/customEmojiServices";
import type { CustomEmoji } from "@/types/workspace";

export interface CustomEmojiUploadProps {
  /** Show the "Add custom emoji" / "Edit custom emoji" title + subtitle. */
  showHeader?: boolean;
  /**
   * Offer the "Add to workspace emoji library" checkbox; a name is only
   * required while it is checked. When false the emoji is always saved to
   * the workspace and the name is always required.
   */
  optionalLibraryAdd?: boolean;
  /** While a preview is showing, "Back" clears the image instead of cancelling. */
  backOnPreview?: boolean;
  /**
   * Edit mode: rename this existing emoji instead of creating one. The image
   * is fixed (the backend only supports renaming) and Save calls the update
   * endpoint.
   */
  editEmoji?: CustomEmoji | null;
  onCancel: () => void;
  /** `emoji` is null when the save didn't add to the workspace library. */
  onSaved: (url: string, emoji: CustomEmoji | null) => void;
  className?: string;
}

/**
 * Custom-emoji upload flow shared by the emoji picker's Upload tab and
 * Settings → Workspace → Emoji. Its ⌘V paste listener is active while
 * mounted, so only mount it when the flow is visible.
 */
export default function CustomEmojiUpload({
  showHeader = false,
  optionalLibraryAdd = false,
  backOnPreview = false,
  editEmoji = null,
  onCancel,
  onSaved,
  className = "",
}: CustomEmojiUploadProps) {
  const workspaceCtx = useWorkspaceContextWithFallback();
  const currentWorkspace = workspaceCtx?.currentWorkspace;
  const customEmojis: CustomEmoji[] = currentWorkspace?.customEmojis ?? [];

  const isEdit = !!editEmoji;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(editEmoji?.name ?? "");
  const [addToWorkspace, setAddToWorkspace] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    preview: pickedPreview,
    blob,
    isCropping,
    cropFile,
    clear,
  } = useSquareEmojiImage({ pasteEnabled: !isEdit });

  const preview = isEdit ? editEmoji.imageUrl : pickedPreview;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void cropFile(file);
    // Reset so the same file can be re-selected after cancel.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearAll = () => {
    clear();
    setName("");
    setAddToWorkspace(false);
  };

  const savesToWorkspace = isEdit || !optionalLibraryAdd || addToWorkspace;

  const nameTaken =
    savesToWorkspace &&
    !!name.trim() &&
    customEmojis.some(
      (e) => e._id !== editEmoji?._id && e.name.toLowerCase() === name.trim().toLowerCase(),
    );

  const canSave =
    !!preview && !isSaving && !isCropping && (!savesToWorkspace || (!!name.trim() && !nameTaken));

  const handleSave = async () => {
    if (!canSave || !preview || !currentWorkspace?._id) return;
    setIsSaving(true);
    try {
      if (isEdit) {
        const updated = await updateCustomEmoji(currentWorkspace._id, editEmoji._id, name.trim());
        workspaceCtx?.setCurrentWorkspace({
          ...currentWorkspace,
          customEmojis: (currentWorkspace.customEmojis || []).map((e) =>
            e._id === editEmoji._id ? updated : e,
          ),
        } as typeof currentWorkspace);
        toast.success("Custom emoji updated");
        onSaved(editEmoji.imageUrl, updated);
        return;
      }
      let url = preview;
      if (blob) {
        const file = blobToFile(blob, "custom-emoji.png");
        url = await uploadCoverImage(file, { noteId: currentWorkspace._id });
      }
      let emoji: CustomEmoji | null = null;
      if (savesToWorkspace) {
        emoji = await createCustomEmoji(currentWorkspace._id, name.trim(), url);
        workspaceCtx?.setCurrentWorkspace({
          ...currentWorkspace,
          customEmojis: [...(currentWorkspace.customEmojis || []), emoji],
        } as typeof currentWorkspace);
        toast.success("Custom emoji added");
      }
      clearAll();
      onSaved(url, emoji);
    } catch (err) {
      toast.error((err as Error).message || (isEdit ? "Failed to update emoji" : "Failed to upload image"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentWorkspace?._id) {
    return (
      <div className={`flex items-center justify-center h-24 text-sm text-gray-400 px-4 text-center ${className}`}>
        Open a workspace to upload images.
      </div>
    );
  }

  return (
    <div className={`flex flex-col text-sm ${className}`}>
      {showHeader && (
        <>
          <div className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? "Edit custom emoji" : "Add custom emoji"}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Custom emoji can be used by anyone in your workspace
          </div>
        </>
      )}

      {!preview ? (
        <>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCropping}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded modal-action-hover hover-component  py-3.5 text-sm text-sec disabled:opacity-60 transition-colors"
          >
            {isCropping ? (
              <Loader2 className="w-[18px] h-[18px] animate-spin" />
            ) : (
              <svg aria-hidden="true" viewBox="0 0 20 20" className="w-[18px] h-[18px] fill-current">
                <path d="M8.5 9.31a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                <path d="M2.375 6.25c0-1.174.951-2.125 2.125-2.125h11c1.174 0 2.125.951 2.125 2.125v7.5a2.125 2.125 0 0 1-2.125 2.125h-11a2.125 2.125 0 0 1-2.125-2.125zM4.5 5.375a.875.875 0 0 0-.875.875v5.491l1.996-1.995a.625.625 0 0 1 .883 0l1.98 1.98 4.137-4.137a.625.625 0 0 1 .883 0l2.871 2.87V6.25a.875.875 0 0 0-.875-.875zm11.875 6.853-3.312-3.313-4.137 4.136a.625.625 0 0 1-.884 0l-1.98-1.98-2.437 2.438v.241c0 .483.392.875.875.875h11a.875.875 0 0 0 .875-.875z" />
              </svg>
            )}
            {isCropping ? "Processing…" : "Upload an image"}
          </button>
          <div className="mt-2.5 text-center text-xs text-faint">
            or {typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl"}+V to paste an image or link
          </div>
        </>
      ) : (
        <>
          <div className="mt-5 rounded bg-gray-100 dark:bg-zinc-800">
            <div className="flex flex-col items-center gap-1 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Preview</div>
              {isEdit ? (
                <div className="w-16 h-16 rounded-lg overflow-hidden">
                  <img src={preview} alt="Preview" className="w-full h-full object-contain" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  title="Choose another image"
                  className="w-16 h-16 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                >
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                </button>
              )}
            </div>
          </div>

          {optionalLibraryAdd && (
            <label className="mt-3 flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={addToWorkspace}
                onChange={(e) => setAddToWorkspace(e.target.checked)}
                className="w-3.5 h-3.5 rounded-[3px] accent-blue-600"
              />
              Add to workspace emoji library
            </label>
          )}
        </>
      )}

      {savesToWorkspace && (preview || !optionalLibraryAdd) && (
        <label className="mt-5 block">
          <div className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
            Emoji name
          </div>
          <input
            autoFocus={optionalLibraryAdd || isEdit}
            type="text"
            autoComplete="off"
            maxLength={50}
            placeholder="emoji name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            className="w-full bg-gray-100 dark:bg-zinc-800 ring-2 ring-gray-200 dark:ring-zinc-700 rounded-md px-2 h-8 text-sm text-gray-900 dark:text-white placeholder-faint outline-none focus:ring-blue-500 dark:focus:ring-blue-500"
          />
          {nameTaken && (
            <div className="mt-2.5 text-xs text-red-500">Name already taken</div>
          )}
        </label>
      )}

      <div className="mt-auto pt-4 pb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => {
            if (backOnPreview && preview) clearAll();
            else onCancel();
          }}
          className="px-3 h-8 rounded-md text-sm text-sec hover-component  transition-colors"
        >
          {backOnPreview && preview ? "Back" : "Cancel"}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="px-4 h-8 rounded-md text-sm font-medium bg-blue-500 hover:bg-blue-700 text-white disabled:opacity-50 disabled:hover:bg-blue-600 transition-colors"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
